/**
 * AI聊天智能创作窗口组件
 *
 * 提供用户与AI智能体交互界面，支持：
 * - 实时消息显示和流式输出
 * - 工具调用过程显示
 * - 思考过程折叠/展开
 * - 模型切换
 * - SSE (Server-Sent Events) 流式通信
 *
 * AI Elements 集成：
 * - 使用 AI Elements 组件 (Conversation, Message, Reasoning, Tool)
 * - 基于 shadcn/ui CSS 变量
 * - Streamdown 用于 Markdown 渲染
 *
 * 注意：所有用户界面文字、提示信息、状态文字必须使用中文
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useChatStore } from '../../store/chatStore';
import { useAgentStore } from '../../store/agentStore';
import { useThemeStore } from '../../store/themeStore';
import { useBranchStore } from '../../store/branchStore';
import { MessageList } from './MessageList';
import { ChatHeader } from './ChatHeader';
import { SessionList } from './SessionList';
import { ChatInput } from './ChatInput';
import { ChatErrorBoundary } from './ChatErrorBoundary';
import { TodoPanel } from './TodoPanel';
import { QuestionPrompt } from './QuestionPrompt';

// Message length limit: 10KB
const MAX_MESSAGE_LENGTH = 10240;

/**
 * Sanitize error message to avoid leaking sensitive information
 */
const sanitizeErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return (error.message || 'Unknown error').substring(0, 100);
  }
  if (typeof error === 'string') {
    return error.replace(/token.*?['"]/gi, '[REDACTED]').substring(0, 100);
  }
  return 'Unknown error';
};

/**
 * Chat window component props interface
 */
interface ChatWindowProps {
  /** Workspace ID */
  workspaceId: number;
  /** JWT authentication token */
  token: string;
}

/**
 * ChatWindow component
 *
 * Fixed on the right side of the page, provides AI Chat interaction interface
 */
const ChatWindowComponent: React.FC<ChatWindowProps> = ({ workspaceId, token }) => {
  const messages = useChatStore((s) => s.messages);
  const streamingMessage = useChatStore((s) => s.streamingMessage);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const loading = useChatStore((s) => s.loading);
  const error = useChatStore((s) => s.error);
  const sendMessage = useChatStore((s) => s.sendMessage);
  const currentModel = useChatStore((s) => s.currentModel);
  const setCurrentModel = useChatStore((s) => s.setCurrentModel);
  const clearError = useChatStore((s) => s.clearError);
  const currentSession = useChatStore((s) => s.currentSession);
  const pendingQuestion = useChatStore((s) => s.pendingQuestion);
  const submitQuestionAnswer = useChatStore((s) => s.submitQuestionAnswer);
  const dismissQuestion = useChatStore((s) => s.dismissQuestion);
  const initialize = useChatStore((s) => s.initialize);

  const [showSessionList, setShowSessionList] = useState(false);

  const { currentBranch } = useBranchStore();
  const prevBranchIdRef = useRef<number | null>(null);

  const chatWindowRef = useRef<HTMLDivElement>(null);

  // ===== Effect Hooks =====

  useEffect(() => {
    initialize(workspaceId);

    return () => {
      useChatStore.getState().cleanup();
    };
  }, [workspaceId, initialize]);

  /**
   * Refresh chat session list when branch switches
   * Clear current session and messages, reload session list for new branch
   */
  useEffect(() => {
    const currentBranchId = currentBranch?.id ?? null;

    // Skip first render (don't refresh during initialization)
    if (prevBranchIdRef.current !== null && prevBranchIdRef.current !== currentBranchId) {
      console.log('[ChatWindow] Branch switch detected, refreshing chat session', {
        previousBranch: prevBranchIdRef.current,
        currentBranch: currentBranchId,
      });

      // Clear current session and messages
      useChatStore.getState().setCurrentSession(null);
      useChatStore.getState().clearMessages();

      // Reload session list
      useChatStore.getState().fetchSessions(token, workspaceId, 1, 20).catch(console.error);
    }

    // Update ref
    prevBranchIdRef.current = currentBranchId;
  }, [currentBranch, token, workspaceId]);

  // ===== Event Handlers =====

  /**
   * Handle sending message
   */
  const handleSendMessage = useCallback(async (content: string) => {
    if (loading || isStreaming) {
      return;
    }

    // Validate message length
    if (content.length > MAX_MESSAGE_LENGTH) {
      console.error(`Message content exceeds length limit (${MAX_MESSAGE_LENGTH} bytes)`);
      return;
    }

    try {
      // Get currently selected agent ID
      const { currentAgentId } = useAgentStore.getState();

      await sendMessage(token, {
        content,
        stream: true,
        workspace_id: workspaceId,
        session_id: currentSession?.id,  // Pass current session ID to fix duplicate session creation issue
        agent_id: currentAgentId || undefined,  // Pass current agent ID
      });
      clearError();
    } catch (err) {
      console.error('Failed to send message:', sanitizeErrorMessage(err));
    }
  }, [loading, isStreaming, sendMessage, token, clearError, currentSession]);

  const handleRetry = useCallback(() => {
    clearError();
    initialize(workspaceId);
  }, [clearError, initialize, workspaceId]);

  /**
   * Toggle session list display
   */
  const handleToggleSessionList = useCallback(() => {
    setShowSessionList((prev) => !prev);
  }, []);

  /**
   * Handle session switch
   */
  const handleSessionSwitch = useCallback((sessionId: string) => {
    console.log('Switched to session:', sessionId);
    setShowSessionList(false);
  }, []);

  /**
   * Regenerate message
   */
  const handleRegenerateMessage = useCallback(async (messageIndex: number) => {
    if (!currentSession || loading || isStreaming) {
      return;
    }

    try {
      // Find the AI message to regenerate
      const aiMessage = messages[messageIndex];
      if (aiMessage.role !== 'assistant') {
        console.warn('Can only regenerate AI messages');
        return;
      }

      // Find the user message before this AI message
      let userMessageIndex = messageIndex - 1;
      while (userMessageIndex >= 0 && messages[userMessageIndex].role !== 'user') {
        userMessageIndex--;
      }

      if (userMessageIndex < 0) {
        console.warn('Corresponding user message not found');
        return;
      }

      const userMessage = messages[userMessageIndex];

      // Delete this AI message and all messages after it
      // TODO: Add deleteMessageRange method in chatStore
      console.log('Regenerate message:', userMessage.content);

      // Resend user message
      await sendMessage(token, {
        session_id: currentSession.id,
        content: userMessage.content,
        model: currentModel,
        stream: true,
        workspace_id: workspaceId
      });
    } catch (error) {
      console.error('Regenerate failed:', error);
    }
  }, [currentSession, loading, isStreaming, messages, sendMessage, token, currentModel]);

  // ===== Theme Configuration =====
  const { resolvedTheme } = useThemeStore();

  // ===== Render Function =====

  return (
    <ChatErrorBoundary
      onError={(error, errorInfo) => {
        console.error('[ChatWindow] Error boundary caught error:', error, errorInfo);
      }}
    >
      <div
        ref={chatWindowRef}
        className="flex flex-col h-full bg-background border-l border-border relative"
        data-theme={resolvedTheme}
      >
        {/* Skip navigation links */}
        <a href="#chat-messages" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-1.5 focus:bg-primary focus:text-primary-foreground focus:rounded focus:text-sm">
          跳转到消息列表
        </a>
        <a href="#chat-input" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-28 focus:z-50 focus:px-3 focus:py-1.5 focus:bg-primary focus:text-primary-foreground focus:rounded focus:text-sm">
          跳转到输入框
        </a>

        {/* ===== Header Toolbar ===== */}
        <ChatHeader
          workspaceId={workspaceId}
          token={token}
          showSessionList={showSessionList}
          onToggleSessionList={handleToggleSessionList}
        />

        {/* Session list dropdown */}
        <SessionList
          workspaceId={workspaceId}
          token={token}
          isOpen={showSessionList}
          onClose={() => setShowSessionList(false)}
          onSessionSwitch={handleSessionSwitch}
        />

        {/* ===== Error Message ===== */}
        {error && (
          <div className="px-4 py-3 bg-destructive/10 border-b border-destructive/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-destructive">{error}</span>
              </div>
              <button
                onClick={handleRetry}
                className="px-3 py-1 text-sm bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors"
              >
                重试
              </button>
            </div>
          </div>
        )}

        <MessageList
          messages={messages}
          sessionId={currentSession?.id}
          streamingMessage={streamingMessage}
          isStreaming={isStreaming}
          loading={loading}
          sessionTitle={currentSession?.title}
          onRegenerateMessage={handleRegenerateMessage}
        />

        {/* ===== Todo Task List ===== */}
        <div className="flex-shrink-0 px-4 py-2 border-t border-border/50">
          <TodoPanel />
        </div>

        {/* ===== Question Component ===== */}
        {pendingQuestion && (
          <div className="flex-shrink-0 px-4 py-2 border-t border-border/50">
            <QuestionPrompt
              question={pendingQuestion}
              onSubmit={(answers) => submitQuestionAnswer(pendingQuestion.question_id, answers)}
              onDismiss={() => dismissQuestion(pendingQuestion.question_id)}
            />
          </div>
        )}

        {!pendingQuestion && (
          <ChatInput
            currentModel={currentModel}
            onModelChange={setCurrentModel}
            onSendMessage={handleSendMessage}
            disabled={loading}
            isStreaming={isStreaming}
            token={token}
            workspaceId={workspaceId}
          />
        )}
      </div>
    </ChatErrorBoundary>
  );
};

ChatWindowComponent.displayName = 'ChatWindow';

export const ChatWindow = ChatWindowComponent;
export default ChatWindow;
